import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useReader } from "../../context/ReaderContext";
import { track } from "../../lib/analytics";
import { ReaderPage, inputCls, btnMain } from "./ReaderShell";

const Box = ({ children }) => <div className="max-w-md rounded-sm border border-border bg-card p-6 sm:p-8">{children}</div>;
const Lbl = ({ htmlFor, children }) => <label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-semibold">{children}</label>;
const Err = ({ msg }) => (msg ? <p role="alert" className="text-sm text-destructive" data-testid="reader-form-error">{msg}</p> : null);

const Verify = ({ token }) => {
  const [state, setState] = useState("busy");
  useEffect(() => {
    api.post("/reader/verify-email", { token }).then(() => setState("ok")).catch(() => setState("bad"));
  }, [token]);
  if (state === "busy") return <p>Potvrđujemo email…</p>;
  return state === "ok" ? (
    <div data-testid="verify-success"><p className="font-semibold">Email je potvrđen.</p><Link to="/prijava" className={`${btnMain} mt-4`} data-testid="verify-login-link">Prijavi se</Link></div>
  ) : (
    <p className="text-destructive" data-testid="verify-failed">Link nije važeći ili je istekao. Prijavi se da zatražiš novi link.</p>
  );
};

export function Registracija() {
  const [params] = useSearchParams();
  const token = params.get("potvrda");
  const [f, setF] = useState({ email: "", password: "", display_name: "", accept_terms: false, marketing_consent: false, website: "" });
  const [done, setDone] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    track("registration_started");
    try {
      const { data } = await api.post("/reader/register", f);
      setDone(data.message);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ReaderPage title={token ? "Potvrda emaila" : "Otvori račun"} intro="Račun je besplatan i služi samo za tvoje postavke, sačuvane članke i obavještenja. Za čitanje vijesti račun nije potreban." testId="register-page" tabs={false}>
      <Box>
        {token ? <Verify token={token} /> : done ? <p data-testid="register-done">{done}</p> : (
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div><Lbl htmlFor="r-name">Ime za prikaz (opcionalno)</Lbl><input id="r-name" value={f.display_name} onChange={set("display_name")} maxLength={60} className={inputCls} data-testid="register-name-input" /></div>
            <div><Lbl htmlFor="r-email">Email</Lbl><input id="r-email" type="email" required value={f.email} onChange={set("email")} className={inputCls} autoComplete="email" data-testid="register-email-input" /></div>
            <div><Lbl htmlFor="r-pw">Lozinka (najmanje 12 znakova, slova i brojevi)</Lbl><input id="r-pw" type="password" required minLength={12} value={f.password} onChange={set("password")} className={inputCls} autoComplete="new-password" data-testid="register-password-input" /></div>
            <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden />
            <label className="flex gap-2 text-sm"><input type="checkbox" required checked={f.accept_terms} onChange={set("accept_terms")} className="mt-1" data-testid="register-terms-checkbox" /><span>Prihvatam uslove korištenja i <Link to="/datenschutz" className="underline">politiku privatnosti</Link>.</span></label>
            <label className="flex gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={f.marketing_consent} onChange={set("marketing_consent")} className="mt-1" data-testid="register-marketing-checkbox" /><span>Opcionalno: želim primati promotivne ponude. (Odvojeno od obavještenja o vijestima; može se povući u svakom trenutku.)</span></label>
            <Err msg={err} />
            <button disabled={busy} className={`${btnMain} w-full`} data-testid="register-submit-button">{busy ? "Šaljem…" : "Otvori račun"}</button>
            <p className="text-sm text-muted-foreground">Već imaš račun? <Link to="/prijava" className="font-semibold underline">Prijavi se</Link></p>
          </form>
        )}
      </Box>
    </ReaderPage>
  );
}

export function Prijava() {
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [pending, setPending] = useState(false);
  const { refresh } = useReader();
  const nav = useNavigate();
  const loc = useLocation();
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setPending(false);
    try {
      await api.post("/reader/login", f);
      await refresh();
      nav(loc.state?.from || "/moj-balkan-info", { replace: true });
    } catch (e2) {
      setErr(errMsg(e2));
      setPending(e2?.response?.status === 403);
    }
  };
  const resend = async () => {
    const { data } = await api.post("/reader/resend-verification", { email: f.email });
    toast.success(data.message);
  };
  return (
    <ReaderPage title="Prijava" intro="Prijava za čitalački račun MOJ BALKAN INFO. Redakcija se prijavljuje odvojeno." testId="reader-login-page" tabs={false}>
      <Box>
        <form onSubmit={submit} className="space-y-4" data-testid="reader-login-form">
          <div><Lbl htmlFor="l-email">Email</Lbl><input id="l-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} autoComplete="email" data-testid="reader-login-email" /></div>
          <div><Lbl htmlFor="l-pw">Lozinka</Lbl><input id="l-pw" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} autoComplete="current-password" data-testid="reader-login-password" /></div>
          <Err msg={err} />
          {pending && <button type="button" onClick={resend} className="text-sm font-semibold underline" data-testid="resend-verification-button">Pošalji ponovo link za potvrdu</button>}
          <button className={`${btnMain} w-full`} data-testid="reader-login-submit">Prijavi se</button>
          <div className="flex justify-between text-sm"><Link to="/zaboravljena-lozinka" className="underline" data-testid="forgot-link">Zaboravljena lozinka?</Link><Link to="/registracija" className="font-semibold underline" data-testid="to-register-link">Otvori račun</Link></div>
        </form>
      </Box>
    </ReaderPage>
  );
}

export function ZaboravljenaLozinka() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [v, setV] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      if (token) {
        await api.post("/reader/reset-password", { token, password: v });
        setMsg("Lozinka je promijenjena. Sve prijave su odjavljene – prijavi se novom lozinkom.");
      } else {
        const { data } = await api.post("/reader/forgot-password", { email: v });
        setMsg(data.message);
      }
    } catch (e2) {
      setErr(errMsg(e2));
    }
  };
  return (
    <ReaderPage title={token ? "Nova lozinka" : "Zaboravljena lozinka"} testId="forgot-page" tabs={false}>
      <Box>
        {msg ? <div data-testid="forgot-done"><p>{msg}</p>{token && <Link to="/prijava" className={`${btnMain} mt-4`}>Prijava</Link>}</div> : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <div><Lbl htmlFor="fp">{token ? "Nova lozinka (najmanje 12 znakova)" : "Email računa"}</Lbl><input id="fp" type={token ? "password" : "email"} required value={v} onChange={(e) => setV(e.target.value)} className={inputCls} data-testid="forgot-input" /></div>
            <Err msg={err} />
            <button className={`${btnMain} w-full`} data-testid="forgot-submit">{token ? "Spremi lozinku" : "Pošalji link"}</button>
          </form>
        )}
      </Box>
    </ReaderPage>
  );
}
